import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle, Search, Newspaper, Star } from "lucide-react";

const MEDIA_LABEL = { tv: "TV", radio: "Rádio", print: "Imprensa", online: "Online" };
const MEDIA_COLOR = {
    tv: "text-rose-600 bg-rose-50",
    radio: "text-violet-600 bg-violet-50",
    print: "text-blue-800 bg-blue-50",
    online: "text-emerald-700 bg-emerald-50",
};

const AdminPress = () => {
    const { authAxios } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = () => {
        setLoading(true);
        authAxios
            .get("/admin/press")
            .then((r) => setItems(r.data))
            .catch(() => toast.error("Erro ao carregar press items"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []); // eslint-disable-line

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Eliminar "${title}"? Esta ação não pode ser desfeita.`)) return;
        try {
            await authAxios.delete(`/admin/press/${id}`);
            toast.success("Item eliminado");
            load();
        } catch {
            toast.error("Erro ao eliminar");
        }
    };

    const filtered = items.filter(
        (i) =>
            i.title.toLowerCase().includes(q.toLowerCase()) ||
            i.media_outlet.toLowerCase().includes(q.toLowerCase()),
    );

    return (
        <div data-testid="admin-press">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--acd-navy-900)]">Press & Imprensa</h1>
                    <p className="text-sm text-[color:var(--acd-muted)]">
                        {items.length} {items.length === 1 ? "item" : "itens"} no total
                    </p>
                </div>
                <div className="flex gap-3">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            className="acd-input pl-10 min-w-[220px]"
                            placeholder="Pesquisar..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="admin-press-search"
                        />
                    </div>
                    <Link
                        to="/admin/press/novo"
                        className="acd-btn-gradient"
                        data-testid="admin-press-new-btn"
                    >
                        <PlusCircle size={16} /> Novo item
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-[color:var(--acd-muted)]">A carregar...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-[color:var(--acd-muted)]">
                        <Newspaper size={36} className="mx-auto mb-3 opacity-20" />
                        Nenhum item encontrado
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[var(--acd-bg-soft)] text-left text-xs uppercase tracking-widest text-[color:var(--acd-muted)]">
                                <tr>
                                    <th className="px-6 py-3">Título</th>
                                    <th className="px-6 py-3">Meio</th>
                                    <th className="px-6 py-3">Tipo</th>
                                    <th className="px-6 py-3">Data</th>
                                    <th className="px-6 py-3 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="border-t border-gray-100 hover:bg-gray-50 transition"
                                        data-testid={`press-row-${item.id?.substring(0, 8)}`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                {item.featured && (
                                                    <Star size={13} className="text-[var(--acd-gold)] shrink-0" fill="currentColor" />
                                                )}
                                                <span className="font-semibold text-[var(--acd-navy-900)] leading-tight line-clamp-1">
                                                    {item.title}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[color:var(--acd-muted)]">
                                            {item.media_outlet}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${MEDIA_COLOR[item.media_type] || "text-gray-600 bg-gray-50"}`}>
                                                {MEDIA_LABEL[item.media_type] || item.media_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[color:var(--acd-muted)] whitespace-nowrap">
                                            {item.date_display}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 justify-end">
                                                <Link
                                                    to={`/admin/press/${item.id}/editar`}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--acd-blue-600)] hover:bg-blue-50 transition"
                                                    title="Editar"
                                                    data-testid={`press-edit-${item.id?.substring(0, 8)}`}
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(item.id, item.title)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition"
                                                    title="Eliminar"
                                                    data-testid={`press-delete-${item.id?.substring(0, 8)}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPress;
